import React, { useRef } from "react";

import { useOnChangeWatcher } from "../models/useOnChangeWatcher";

import Page from "../models/Page";
import Consumer from "../models/Consumer";
import ConnectorGrab from "../components/ConnectorGrab";
import EditableText from "../components/EditableText";
import {
  onConnectionTargetMouseLeave,
  onConnectionTargetMouseMove,
  onConnectionTargetMouseUp,
} from "./ConnectionTarget";
import { ConnectionType, dehighlightConnections, highlightConnections } from "../models/Connection";
import { onKeyDown_ButtonOrActor } from "../components/DrawingArea";

import { showToast } from "../components/Toasts";
import Logger from "../Logger";
import Browser from "../browser";
import { DragMode } from "../models/TreeviewModel";
import BaseModel from "../models/BaseModel";

import { bringElementToTop } from "./GraficElementUtils";
import ConnectableObject from "../models/ConnectableObject";
import Room from "../models/Room";

import { isUiModeExpert } from "../uiMode";

import Button from "../models/Button";
import { itx, noItx } from "../translationItx/itx";

const logger = new Logger();

export function moveConnectableObjectTo(consumerRef: HTMLDivElement, x: number, y: number) {
  consumerRef.style.left = `${x}px`;
  consumerRef.style.top = `${y}px`;
}

export default function ConsumerView(props: { connetableObject: ConnectableObject; page: Page; mainPage: Page }) {
  const { connetableObject, page, mainPage } = props;

  const thisElement = useRef<HTMLDivElement>(null);
  const dragoverZoomElement = useRef<HTMLDivElement>(null);

  const consumer = connetableObject.isConsumer ? (connetableObject as Consumer) : undefined;
  const buttonOrSensor = connetableObject.isButton ? (connetableObject as Button) : undefined;

  logger.log(`render consumerEditView ${connetableObject.tag}`);

  let fontSize = 16; // Feste Schriftgröße da showIcons immer true

  const imgSrc = connetableObject.getImgSrc();

  let symbolWidth = 1;

  var imageStyle: any = {
    width: symbolWidth + "cm", // no itx
    height: symbolWidth + "cm", // no itx
    objectFit: "contain",
  };

  const borderSize = 5;

  connetableObject.center.x = 0;
  connetableObject.center.y = 0.5 + borderSize;

  var imgElm = imgSrc ? <img draggable="false" style={imageStyle} src={imgSrc} /> : null; // no itx

  function upperCase(): boolean {
    if (connetableObject.tag != "") {
      return true;
    }
    return false;
  }

  let selectFrameClassName = "selectFrame selected"; // no itx - immer selected

  const connectedToChannel = connetableObject.getConnectedChannel() != undefined;
  let tooltipText: string | undefined;

  if (!connectedToChannel) {
    selectFrameClassName += " noChannelConnection"; // no itx

    tooltipText = itx("consumerView.consumerNotConnectedWithChannel", "Der Verbraucher ist mit keinem Kanal verbunden");
  }

  const styleConsumer: any = {
    cursor: "auto",
    pointerEvents: "auto",
    backgroundColor: "var(--panel-background-color)",
    transform: "scale(0.8)", // no itx -
  };

  const borderColor = connetableObject?.drawingItemColor() || "var(--drawingItemColor-Button)";

  const outerStyle: any = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderRadius: "5px",
    padding: "10px", // immer 10px da immer selected
    pointerEvents: "auto",
    border: "3px solid " + borderColor,
    backgroundColor: "var(--panel-background)",
  };

  // showIcons ist immer true - keine bedingte Styling mehr nötig

  function ChannelNotSameCabinetThanRoom(props: {}) {
    let channelsInOtherCabinet: undefined | ConnectableObject;
    connetableObject.connections.forEach((connection) => {
      const channel = connection.getChannel();
      if (channel?.parent?.parent && (connetableObject.parent as Room)?.cabinet != channel?.parent?.parent) {
        channelsInOtherCabinet = channel;
      }
    });
    if (channelsInOtherCabinet) {
      return (
        <div
          className="channelsInOtherCabinet"
          style={{
            position: "absolute",
            right: "100%",
            top: "25%",
            display: "flex",
            flexDirection: "column",
            width: "max-content",
            border: "1px solid black",
            borderRadius: "2px",
            padding: "2px",
            alignSelf: "center",
          }}
        >
          <div style={{ display: "flex", flexDirection: "row" }}>
            <span style={{ fontSize: "0.5rem", paddingRight: "5px" }}>
              {channelsInOtherCabinet?.fullTag} {channelsInOtherCabinet?.name}
            </span>
          </div>
        </div>
      );
    }
    return null;
  }

  function QuantityButtons(props: {}) {
    useOnChangeWatcher(consumer);

    function QuantitiButton(
      props: { direction: "plus" | "minus" }, // no itx
    ) {
      const { direction } = props;
      function changeQuantity() {
        if (consumer) {
          if (direction == "plus") {
            consumer.quantity = consumer.quantity + 1;
          } else {
            consumer.quantity = consumer.quantity - 1;
          }
          consumer.raiseOnChange("quantity");
        }
      }

      return (
        <button
          id={direction == "plus" ? "qPlus" : "qMinus"} // no itx
          className="button small circle" // no itx
          style={{
            marginRight: direction == "minus" ? "10px" : undefined,
            marginLeft: direction == "plus" ? "10px" : undefined,
          }}
          onMouseDown={(ev) => {
            ev.stopPropagation();
          }}
          onKeyDown={(ev) => {
            ev.stopPropagation();
          }}
          onClick={(ev) => {
            ev.stopPropagation();
            changeQuantity();
          }}
          onDoubleClick={(ev) => {
            // to prevent propertie opening
            ev.stopPropagation();
            ev.preventDefault();
          }}
        >
          <i className={`las la-${direction}-circle`} />
        </button>
      );
    }

    // Immer anzeigen da immer selected
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
          fontSize: `${fontSize}px`,
        }}
        data-noitx="true" // no itx
      >
        <QuantitiButton
          direction="minus" // no itx
        />

        {consumer && <span>{consumer.quantity || 1}</span>}

        <QuantitiButton
          direction="plus" // no itx
        />
      </div>
    );
  }

  function onMouseMove(ev: any) {
    if (!print && ev.buttons == 1 && thisElement.current) {
      onConnectionTargetMouseMove(page, connetableObject, thisElement.current, ConnectionType.FromSourceDevice);
    }
  }

  function onMouseUp() {
    if (!print && thisElement.current) {
      let connectionTarget = ConnectionType.FromSourceDevice;

      onConnectionTargetMouseUp(connetableObject, thisElement.current, ConnectionType.FromSourceDevice);
    }
  }

  function onClickGrab() {
    //
    // deactiviet bis die Funktion wirklick implementiert ist
    // mwe 25.11.2024
    //
    // state.dialogManager.openDialog(<ConnectDialog page={page} consumer={consumer} />);
  }

  const touchSelected = Browser.isTouch() ? "touchSelected" : ""; // no itx

  async function handleDragEnter(mode: DragMode, sData: string) {
    try {
      const data = JSON.parse(sData);

      if (data.id) {
        const obj = state.objectRegistry.getObject(data.id) as BaseModel;
        if (obj) {
          switch (obj.className) {
            case noItx("Fuse"):
            case noItx("Terminal"):
              dragoverZoomElement.current?.classList.add("zoomOnDragOver"); // no itx
              break;
          }
        }
      }
    } catch (ex) {
      logger.error(ex);
    }
  }
  async function handleDrop(mode: DragMode, sData: string) {
    let txt = state.translate("treeViewModel.dragADropforbidden", `Drag&Drop ist nicht erlaubt`);
    try {
      const data = JSON.parse(sData);

      if (data.id) {
        const obj = state.objectRegistry.getObject(data.id) as BaseModel;
        const consumerOrButton = consumer || buttonOrSensor;
        if (obj && consumerOrButton) {
          const res = await consumerOrButton.handleDrop2(mode, obj);
          if (res.errorMessage) {
            txt += ":\n" + res.errorMessage;
          }
          if (res.success) {
            if (res.errorMessage) {
              showToast({
                isError: false,
                text: res.errorMessage,
                time: 5000,
              });
            }
            return;
          }
        }
      }
    } catch (ex) {
      logger.error(ex);
    }

    showToast({
      isError: true,
      text: txt,
    });
  }

  function onDragEnter(e: any) {
    try {
      if (state.dragData) {
        const mode = e.dataTransfer.effectAllowed;
        handleDragEnter(mode, state.dragData);
        return;
      }
    } catch (ex) {
      logger.error(ex);
    }
  }

  function onDragLeave(e: any) {
    dragoverZoomElement.current?.classList.remove("zoomOnDragOver"); // no itx
  }

  function onDrop(e: any) {
    try {
      var items = e.dataTransfer.items;
      const item = items[0];
      if (item.kind === "string") {
        item.getAsString(function (sData: string) {
          const mode = e.dataTransfer.effectAllowed;
          handleDrop(mode, sData);
        });
      }

      dragoverZoomElement.current?.classList.remove("zoomOnDragOver"); // no itx
    } catch (ex) {
      logger.error(ex);
    }

    e.preventDefault();
  }
  function onContextMenu(ev: any) {
    state.contextMenuManager.show(ev, connetableObject);
  }

  const editableTextStyle = {
    marginLeft: "10px",
    paddingLeft: "5px",
    paddingRight: "5px",
    width: "100%",
    minWidth: "50px",
    textAlign: "left",
  };

  // Wird bei Enter im Inline-Editor (Tag/Name) aufgerufen.
  // Ziel: Edit-Overlay schließen und den Consumer visuell in den Zustand "nicht ausgewählt" bringen.
  function finishInlineEdit() {
    // Inline-Edit-Container ausblenden
    state.currentEditObject.value = undefined;
    // Auswahl im SelectionManager zurücksetzen
    state.selectionManager.selectedObject = undefined;
    // Verbindungs-Highlights entfernen
    dehighlightConnections(true, page, connetableObject);
    // Fokus vom editierbaren Feld nehmen, damit kein Focus-Stil aktiv bleibt
    if (document?.activeElement) {
      (document.activeElement as HTMLElement)?.blur();
    }
  }

  return (
    <div
      id={connetableObject.id}
      style={{
        position: "relative",
      }}
      ref={thisElement}
    >
      {/* ConnectionsTextView wird nicht angezeigt da immer selected */}
      <div
        className={`consumer connectionTarget ${touchSelected} selected`}
        id={"connectionTargetConsumer" + connetableObject.id}
        style={styleConsumer}
        onContextMenu={onContextMenu}
        onDrop={onDrop}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onMouseEnter={(ev) => {
          if (print == undefined && ev.buttons == 0) {
            highlightConnections(true, page, connetableObject);
            // diagnosticMode ist immer true - bringElementToTop wird immer aufgerufen
            bringElementToTop(thisElement.current);
          }
        }}
        onMouseLeave={(ev) => {
          console.log("consumer on mouse leave");
          if (print == undefined && ev.buttons == 0) {
            dehighlightConnections(true, page, connetableObject);
          }
          if (print == undefined && ev.buttons == 1 && thisElement.current) {
            onConnectionTargetMouseLeave(thisElement.current);
          }
        }}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
      >
        <div
          className="port"
          style={{ position: "absolute", top: "50%", left: "50%", width: "0px", height: "0px" }}
        ></div>

        {/* ------------------------------------------------------------ */}

        <div
          // className={selectFrameClassName}
          style={{ boxShadow: "var(--panel-shadow)", borderRadius: "5px" }}
          ref={dragoverZoomElement}
          tabIndex={1}
          onKeyDown={(ev) => {
            if (!print) {
              onKeyDown_ButtonOrActor(ev);
            }
          }}
        >
          <ConnectorGrab
            mainPage={mainPage}
            page={page}
            connectableObject={connetableObject}
            x={0}
            portName="L"
            direction={180}
            // size={0.5}
            type={ConnectionType.FromSourceDevice}
            ownerSelected={true}
            disabled={false}
            onClick={onClickGrab}
          />

          <ConnectorGrab
            mainPage={mainPage}
            page={page}
            connectableObject={connetableObject}
            x={100}
            type={ConnectionType.FromSourceDevice}
            portName="R"
            // size={0.5}
            ownerSelected={true}
            disabled={false}
            onClick={onClickGrab}
          />

          <div
            style={outerStyle}
            id={"selectionHandler" + connetableObject.id}
            onDoubleClick={(ev) => {
              ev.stopPropagation();
              state.selectionManager.selectedObject = connetableObject;
              connetableObject.treeViewModel.handleOpen();
            }}
            onClick={(ev) => {
              // diagnosticMode ist immer true - vereinfachte Logik
              ev.stopPropagation();

              //
              // use timeout to avoid disturbing of double click
              //
              setTimeout(() => {
                state.selectionManager.selectedObject = connetableObject;
              }, 500);
            }}
          >
            <div
              style={{
                position: "relative",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                paddingBottom: "10px",
              }}
              id={"innerDivConsumer" + connetableObject.id}
            >
              <div
                className={"consumerImage"} // no itx
                id={"consumerImage" + connetableObject.id}
              >
                {imgElm}
              </div>

              <EditableText
                key={"tag" + connetableObject.id}
                obj={connetableObject}
                property="tagOrName" // no itx
                propertyType="text"
                placeholder={noItx("Tag")}
                multiline={false}
                // style={{ borderRadius: "5px" }}
                maxCharacters={state.maxCharLimit.text}
                showBorder={true}
                disableEdit={false}
                upperCase={upperCase()}
                checklistValueDouble={connetableObject.getAllTagsFromSameTypeWithoutOwn.bind(connetableObject)}
                showToastX={showToast}
                emptyForbidden={true}
                style={{ ...editableTextStyle }}
                // Enter im Tag-Feld: Wert übernehmen (über EditableText) und Edit-Modus beenden
                onEnterkey={finishInlineEdit}
              />
            </div>

            <EditableText
              key={"name" + connetableObject.id}
              obj={connetableObject}
              property="name" // no itx
              propertyType="text"
              multiline={false}
              showBorder={true}
              disableEdit={false}
              placeholder={state.translate("name", "Name")}
              style={{ ...editableTextStyle }}
              // Enter im Name-Feld: Wert übernehmen (über EditableText) und Edit-Modus beenden
              onEnterkey={finishInlineEdit}
            />
            <div style={{ height: "10px" }} />

            {isUiModeExpert() && consumer && consumer.fuse && (
              <div key="fuse" style={{ marginBottom: "10px" }}>
                {consumer.fuse.tag}
              </div>
            )}

            {consumer && <QuantityButtons />}
          </div>
        </div>
      </div>
    </div>
  );
}
